import { useContext, useEffect, useRef } from "react";
import styled from "styled-components";
import backgroundRunnersParameters from "components/backgroundRunners/backgroundRunnersParameters.json";
import { isCursorCollisioned } from "components/backgroundRunners/cursorCollision";
import { normalizeAngle } from "components/backgroundRunners/angleNormalization";
import { getRicochetAngle } from "components/backgroundRunners/angleRicochet";
import { MarginHitContext } from "contexts/marginHitContext";
import { constants } from "constants";

const StyledBackground = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
`;

const StyledCanvas = styled.canvas`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
`;

const StyledWall = styled.div`
  position: absolute;
  transition: box-shadow 160ms ease;
`;

const createRunners = (maximumX, maximumY) =>
  Array.from({ length: backgroundRunnersParameters.runnersCount }, (_, index) => ({
    color: index === backgroundRunnersParameters.runnersCount - 1
      ? constants.colors.essenceTransparent
      : constants.colors.bitTransparent,
    x: maximumX * Math.random(),
    y: maximumY * Math.random(),
    angle: normalizeAngle(2 * Math.PI * Math.random()),
    speed: backgroundRunnersParameters.minSpeed,
  }));

const drawCircle = (context, runner) => {
  const radius = backgroundRunnersParameters.runnerRadius;
  context.beginPath();
  context.fillStyle = runner.color;
  context.arc(runner.x + radius, runner.y + radius, radius, 0, 2 * Math.PI);
  context.fill();
  context.lineWidth = 0.5;
  context.strokeStyle = runner.color;
  context.stroke();
};

export const BackgroundRunners = () => {
  const canvasRef = useRef(null);
  const runnersRef = useRef([]);
  const cursorRef = useRef({ x: -100, y: -100 });
  const frameRef = useRef(0);
  const lastFrameRef = useRef(0);
  const { hitMargin, isTopMarginHit, isBottomMarginHit, isLeftMarginHit, isRightMarginHit } =
    useContext(MarginHitContext);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const context = canvas.getContext("2d", { alpha: true });
    const radius = backgroundRunnersParameters.runnerRadius;
    let maximumX = 0;
    let maximumY = 0;
    let paused = document.hidden;

    const resize = () => {
      const width = Math.max(1, Math.round(canvas.clientWidth));
      const height = Math.max(1, Math.round(canvas.clientHeight));
      if (canvas.width === width && canvas.height === height) return;
      canvas.width = width;
      canvas.height = height;
      maximumX = Math.max(0, width - radius * 2);
      maximumY = Math.max(0, height - radius * 2);
      runnersRef.current = createRunners(maximumX, maximumY);
      runnersRef.current.forEach((runner) => drawCircle(context, runner));
    };

    const updatePointer = ({ clientX, clientY }) => {
      cursorRef.current = { x: clientX, y: clientY };
    };

    const updateVisibility = () => {
      paused = document.hidden;
      if (!paused && !frameRef.current) frameRef.current = requestAnimationFrame(animate);
    };

    const animate = (time) => {
      frameRef.current = 0;
      if (paused) return;

      if (time - lastFrameRef.current >= backgroundRunnersParameters.updateInterval) {
        lastFrameRef.current = time;
        const cursorYWithOffset = cursorRef.current.y + window.scrollY;

        runnersRef.current.forEach((runner) => {
          context.clearRect(runner.x - 1, runner.y - 1, radius * 2 + 2, radius * 2 + 2);
        });

        runnersRef.current = runnersRef.current.map((runner) => {
          let nextRunner = runner;

          if (isCursorCollisioned(nextRunner, {
            cursorX: cursorRef.current.x,
            cursorYWithOffset,
          })) {
            const hitAngle = normalizeAngle(Math.atan2(
              -(cursorYWithOffset - (nextRunner.y + radius)),
              cursorRef.current.x - (nextRunner.x + radius)
            ));
            nextRunner = {
              ...nextRunner,
              angle: normalizeAngle(hitAngle + Math.PI),
              speed: backgroundRunnersParameters.hitSpeed,
            };
          }

          const hitTop = nextRunner.y <= 0;
          const hitBottom = nextRunner.y >= maximumY;
          const hitLeft = nextRunner.x <= 0;
          const hitRight = nextRunner.x >= maximumX;
          if (hitTop) hitMargin("top");
          if (hitBottom) hitMargin("bottom");
          if (hitLeft) hitMargin("left");
          if (hitRight) hitMargin("right");

          if (hitTop || hitBottom || hitLeft || hitRight) {
            const ricochet = getRicochetAngle(
              nextRunner.x,
              nextRunner.y,
              maximumX,
              maximumY,
              nextRunner.angle
            );
            nextRunner = {
              ...nextRunner,
              x: ricochet.ricochetX,
              y: ricochet.ricochetY,
              angle: ricochet.ricochetAngle,
            };
          }

          const nextSpeed = nextRunner.speed / backgroundRunnersParameters.speedDevider;
          return {
            ...nextRunner,
            x: nextRunner.x + Math.cos(nextRunner.angle) * nextRunner.speed,
            y: nextRunner.y - Math.sin(nextRunner.angle) * nextRunner.speed,
            speed: nextSpeed > backgroundRunnersParameters.minSpeed
              ? nextSpeed
              : nextRunner.speed,
          };
        });

        runnersRef.current.forEach((runner) => drawCircle(context, runner));
      }

      frameRef.current = requestAnimationFrame(animate);
    };

    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(canvas);
    window.addEventListener("pointermove", updatePointer, { passive: true });
    document.addEventListener("visibilitychange", updateVisibility);
    resize();
    frameRef.current = requestAnimationFrame(animate);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("pointermove", updatePointer);
      document.removeEventListener("visibilitychange", updateVisibility);
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
    };
  }, [hitMargin]);

  const glow = `0 0 8px ${constants.colors.almostWhite}66`;

  return (
    <StyledBackground aria-hidden="true">
      <StyledCanvas ref={canvasRef} />
      <StyledWall style={{ inset: "0 0 auto", height: 1, boxShadow: isTopMarginHit ? glow : "none" }} />
      <StyledWall style={{ inset: "auto 0 0", height: 1, boxShadow: isBottomMarginHit ? glow : "none" }} />
      <StyledWall style={{ inset: "0 auto 0 0", width: 1, boxShadow: isLeftMarginHit ? glow : "none" }} />
      <StyledWall style={{ inset: "0 0 0 auto", width: 1, boxShadow: isRightMarginHit ? glow : "none" }} />
    </StyledBackground>
  );
};
